import GenericModal from './GenericModal';
import { getAccountTypeIcon } from '../withdrawals/PayoutWithdrawalMethodItem';
import { camelCaseToSentenceCase, removeUnderscoreAndCapitalizeFirstLetters } from 'utils/string';
import Star from 'icons/Star';
import { IWithdrawalMethod } from 'types';
import { WithdrawalAccountType } from 'lib/constants';
import { displayAddress } from 'utils/address';
import { isSupportAdminMode, isSuperAdminMode } from 'utils/user';

const labelMap = { legalType: 'Entity Type', abartn: 'Routing Number' };
export default function WithdrawalMethodDetailsModal({
  open,
  onClose,
  method,
  isPreferred,
}: {
  open: boolean;
  onClose: () => void;
  method: IWithdrawalMethod;
  isPreferred: boolean;
}) {
  if (method.accountType === WithdrawalAccountType.THERA_WALLET) {
    return null;
  }

  const methodDetails = Object.entries(method?.details)

    .filter(
      ([item, value]) =>
        (item !== 'forceWire' || isSupportAdminMode || isSuperAdminMode) &&
        item !== 'corpay_template_id' &&
        value !== '' &&
        value !== null &&
        value !== undefined,
    )
    .map(([name, value]) => {
      let _name = '';
      let _value = value;
      if (labelMap[name]) {
        _name = labelMap[name];
      } else {
        _name = camelCaseToSentenceCase(name);
      }
      // Format PIX account types
      if (name === 'account_type' && typeof value === 'string' && value.startsWith('Pix_')) {
        switch (value) {
          case 'Pix_email':
            _value = 'PIX Email';
            break;
          case 'Pix_phone':
            _value = 'PIX Phone';
            break;
          case 'Pix_document_id':
            _value = 'PIX Document ID';
            break;
          case 'Pix_bank_account':
            _value = 'PIX Bank Account';
            break;
          default:
            _value = value.replace('Pix_', 'PIX ').replace(/_/g, ' ');
        }
      }
      return { [_name]: _value };
    });

  // Payoneer methods carry a placeholder recipient address (Payoneer pays the payee account, not a bank address),
  // so don't surface it in the details view.
  const addressDisplay =
    method.accountType !== WithdrawalAccountType.PAYONEER &&
    method.recipientAddress &&
    displayAddress(method.recipientAddress);

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      showHeaderBorder={false}
      width="min-w-[95vw] md:min-w-min max-w-[98vw]"
      title={
        <div className="flex items-center gap-4">
          <p className="flex h-12 w-12 items-center justify-center rounded-full border border-neutral-200 transition">
            {getAccountTypeIcon(method.accountType)}
          </p>
          <div className="flex flex-col gap-1">
            {method.accountType === 'DOLARAPP'
              ? 'DolarApp'
              : method.accountType === 'DLOCAL_STABLECOIN'
                ? 'Stablecoin'
                : removeUnderscoreAndCapitalizeFirstLetters(method.accountType)}
            {isPreferred && (
              <div className="flex items-center justify-center rounded-full bg-therablue px-2.5 py-0.5 text-white">
                <Star />
                <span className="ml-2 text-xs font-medium">Preferred Method</span>
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="flex w-[85vw] flex-col gap-5 md:w-[480px]">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4 rounded-lg bg-neutral-50 p-4">
            <p className="shrink-0 text-sm font-medium text-neutral-500">Account Holder Name</p>
            <p className="text-right text-sm font-medium text-neutral-800" data-sensitive-copy-admins>
              {method.accountHolderName}
            </p>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-lg bg-neutral-50 p-4">
            <p className="shrink-0 text-sm font-medium text-neutral-500">Currency</p>
            <p className="text-right text-sm font-medium text-neutral-800">{method.currency}</p>
          </div>
          {method.title && (
            <div className="flex items-center justify-between gap-4 rounded-lg bg-neutral-50 p-4">
              <p className="shrink-0 text-sm font-medium text-neutral-500">Type</p>
              <p className="text-right text-sm font-medium text-neutral-800">{method.title}</p>
            </div>
          )}

          {methodDetails.map((detail) => {
            const name = Object.keys(detail)[0];
            const value = Object.values(detail)[0];
            return (
              <div key={name} className="flex items-center justify-between gap-4 rounded-lg bg-neutral-50 p-4">
                <p className="shrink-0 text-sm font-medium text-neutral-500">{name}</p>
                <p className="text-right text-sm font-medium text-neutral-800" data-sensitive-copy-admins>
                  {value}
                </p>
              </div>
            );
          })}
          {addressDisplay && (
            <div className="flex items-center justify-between gap-4 rounded-lg bg-neutral-50 p-4">
              <p className="shrink-0 text-sm font-medium text-neutral-500">Address</p>
              <p className="text-right text-sm font-medium text-neutral-800">{addressDisplay}</p>
            </div>
          )}
        </div>
      </div>
    </GenericModal>
  );
}
